import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AiImagesAdminService } from '../../../services/ai-images-admin.service';
import { BusinessImageUsage, ImageGenerationRow } from '../../../models/ai-images.model';
import { BusinessAddOn, Plan } from '../../../models/plan.model';
import { UserSummary } from '../../../models/user.model';
import { dayRangeUtc, errorMessage, formatUsd, monthToDate } from '../../agents/agents-format';
import {
  aiImagesAddOns,
  countedImages,
  generationStatusLabel,
  generationStatusPill,
  monthLabel,
  purposeLabel,
  slotLabel,
  thumbnailsOf,
  usageCostUsd,
  utcInstant
} from '../../ai/images/ai-images-format';

/**
 * Businesses → profile → "AI images" (specs/agents CONTRACT-v1.4 §9, FR-47.2, mockups-v1.4 S8). Read-only:
 * the owner's AI images add-on(s), this UTC month's allowance — used, reserved and left, shared by every
 * business of the account — what the month cost Plus in USD, and each request with its thumbnails,
 * status, who asked, when, and the refusal code.
 *
 * The add-ons come from the profile's own add-on list and plans (passed in, so the page makes no second
 * call); everything else from the AI API's staff route `GET api/admin/images/usage` (§5.4). Agents dark
 * (503), no answer, or an AI API without the v1.4 routes (404) shows one quiet line, as the Plus
 * Intelligence and Websites panels do. Costs and the model are platform staff facts (A20): owners never see them.
 */
@Component({
  selector: 'app-business-ai-images-panel',
  standalone: false,
  templateUrl: './ai-images-panel.component.html',
  styleUrls: ['../intelligence-panel/intelligence-panel.component.css', './ai-images-panel.component.css']
})
export class BusinessAiImagesPanelComponent implements OnChanges {
  @Input() businessId?: string;
  /** The owner's add-ons as the profile loaded them (`GET api/BusinessManagement/{id}/addons`). */
  @Input() addOns: BusinessAddOn[] | null = null;
  /** The add-on plans (with their features), to tell the AI images ones apart. */
  @Input() addOnPlans: Plan[] | null = null;
  /** The business's users, to show who asked by name. */
  @Input() users: UserSummary[] | null = null;

  usage: BusinessImageUsage | null = null;
  loading = false;
  error = '';
  /** Set when the AI API answers 503, 404 or not at all; replaces the usage body. */
  unavailable = '';

  readonly usd = formatUsd;
  readonly statusPill = generationStatusPill;
  readonly statusLabel = generationStatusLabel;
  readonly counted = countedImages;
  readonly purpose = purposeLabel;
  readonly slot = slotLabel;
  readonly thumbs = thumbnailsOf;
  readonly utc = utcInstant;
  /** "Oct 2026" for the month the figures are for. */
  monthName = monthLabel(monthToDate().from);

  constructor(private images: AiImagesAdminService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) this.load();
  }

  load(): void {
    this.usage = null;
    this.error = '';
    this.unavailable = '';
    const id = this.businessId;
    if (!id) return;

    // The allowance is the UTC month's (it resets on the first UTC day, AC-45), so the range is too.
    const month = monthToDate();
    this.monthName = monthLabel(month.from);
    const range = dayRangeUtc(month.from, month.to);
    this.loading = true;
    this.images.getUsage(id, range.from!, range.to!).subscribe({
      next: usage => {
        this.usage = usage ?? null;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        if (err?.status === 404) {
          this.unavailable = 'The AI API does not report image usage yet (agent platform v1.4 is not deployed there).';
          return;
        }
        if (err?.status === 503) {
          this.unavailable = 'Agents are switched off on the AI API.';
          return;
        }
        if (err?.status === 0) {
          this.unavailable = 'The AI API did not answer (network or CORS).';
          return;
        }
        this.error = errorMessage(err, "Could not load this month's image usage.");
      }
    });
  }

  /** The owner's live AI images add-ons. */
  get imageAddOns(): BusinessAddOn[] {
    return aiImagesAddOns(this.addOns, this.addOnPlans);
  }

  /** "60 a month" per add-on when the plan carries its allowance. */
  addOnAllowance(addOn: BusinessAddOn): number | null {
    const plan = (this.addOnPlans ?? []).find(p => p.planID === addOn.planId);
    const n = plan?.monthlyImageAllowance;
    return typeof n === 'number' && n > 0 ? n : null;
  }

  get generations(): ImageGenerationRow[] {
    return this.usage?.generations ?? [];
  }

  get costUsd(): number | null {
    return usageCostUsd(this.usage);
  }

  /** 0–100 share of the allowance used or reserved; null without an allowance. */
  get usedPct(): number | null {
    const allowance = this.usage?.allowance ?? 0;
    if (!(allowance > 0)) return null;
    const taken = (this.usage?.used ?? 0) + (this.usage?.reserved ?? 0);
    return Math.min(100, Math.round((taken / allowance) * 100));
  }

  get exhausted(): boolean {
    const u = this.usage;
    return !!u && u.allowance > 0 && u.left <= 0;
  }

  /** The requester's name when they are one of the business's users, else the stored id. */
  who(requestedBy: string | null | undefined): string {
    if (!requestedBy) return '—';
    const user = (this.users ?? []).find(u => `${u.userId}`.toLowerCase() === requestedBy.toLowerCase());
    if (!user) return requestedBy;
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email || requestedBy;
  }
}
